import { z } from 'zod/v4';

export const SlcpAssessmentParamsSchema = z.object({
  id: z.string().min(1),
});

export type SlcpAssessmentParams = z.infer<typeof SlcpAssessmentParamsSchema>;

/**
 * One SLCP assessment with its full answer `object`
 * (`GET /api/slcp/assessments/:id`). Same fields as the legacy
 * `GET /v1/slcp-assessments/:id`, except that absent values are `null` — the
 * legacy serializer printed `''` for a missing string or date.
 */
export const SlcpAssessmentDetailSchema = z.object({
  id: z.string(),
  companyId: z.string().nullable(),
  companyName: z.string(),
  objectId: z.string().nullable(),
  facilityId: z.string().nullable(),
  status: z.string().nullable(),
  revision: z.string().nullable(),
  version: z.string().nullable(),
  object: z.record(z.string(), z.unknown()).nullable(),
  notes: z.unknown(),
  selectedVerifier: z.unknown(),
  firstVrcOn: z.string().nullable(),
  startedOn: z.string().nullable(),
  submittedOn: z.string().nullable(),
});

export type SlcpAssessmentDetail = z.infer<typeof SlcpAssessmentDetailSchema>;

/**
 * The SLCP-issued report link for one assessment
 * (`GET /api/slcp/assessments/:id/attachment`) — the same body as the legacy
 * `GET /v1/slcp-assessments/:objectId/attachment`, now under `{ data }`.
 */
export const SlcpAssessmentAttachmentSchema = z.object({
  url: z.string(),
});

export type SlcpAssessmentAttachment = z.infer<
  typeof SlcpAssessmentAttachmentSchema
>;

/**
 * `POST /api/slcp/assessments/:id/history` body — the data points that could
 * not be synced, if any. The companies are no longer sent: the legacy
 * `POST /v1/slcp-assessments/history` took `forCompanyId`, `byCompanyId` and
 * `onBehalfOfCompanyId` from the body unchecked, so a caller could record a
 * sync as any company's. They are derived now — see
 * `SlcpAssessmentsService.recordSync`.
 */
export const CreateSlcpSyncHistoryBodySchema = z.object({
  failedDatapoints: z
    .array(z.record(z.string(), z.unknown()))
    .max(5000)
    .optional(),
});

export type CreateSlcpSyncHistoryBody = z.infer<
  typeof CreateSlcpSyncHistoryBodySchema
>;

/** One recorded SLCP profile sync. */
export const SlcpSyncHistorySchema = z.object({
  id: z.string(),
  slcpAssessmentId: z.string(),
  slcpAssessmentObjectId: z.string(),
  forCompanyId: z.string(),
  byCompanyId: z.string(),
  onBehalfOfCompanyId: z.string(),
  revision: z.string(),
  failedDatapoints: z.array(z.record(z.string(), z.unknown())).nullable(),
  createdAt: z.string().nullable(),
});

export type SlcpSyncHistory = z.infer<typeof SlcpSyncHistorySchema>;

/**
 * The company's own most recent SLCP assessment with the syncs it recorded for
 * it (`GET /api/slcp/assessments/own/latest`), or `null` when it has no
 * approved facility or no assessment. Replaces the legacy
 * `GET /v1/slcp-assessments` list, whose one caller read only row 0.
 */
export const LatestOwnSlcpAssessmentSchema = z
  .object({
    id: z.string(),
    objectId: z.string().nullable(),
    facilityId: z.string().nullable(),
    status: z.string().nullable(),
    revision: z.string().nullable(),
    version: z.string().nullable(),
    firstVrcOn: z.string().nullable(),
    startedOn: z.string().nullable(),
    submittedOn: z.string().nullable(),
    history: z.array(SlcpSyncHistorySchema),
  })
  .nullable();

export type LatestOwnSlcpAssessment = z.infer<
  typeof LatestOwnSlcpAssessmentSchema
>;
